import { useCallback, useEffect, useState } from "react";
import * as Y from "yjs";

/**
 * useChannelLocks — Phase-3 per-channel exclusive-control mechanism.
 *
 * Any peer can claim a channel by writing `lockedBy: <theirClientId>` into
 * the shared Y.Map("channels").<idx>.set("lockedBy", clientId). While
 * a channel is locked, OTHER peers' UI write-gates skip mixer mutations on
 * that channel (volume / mute / pan / solo). The lock auto-releases when
 * the holder's awareness state drops (i.e. they leave the room or close
 * the tab) — we observe `awarenessStates` and clear any lockedBy whose
 * clientId no longer has a live presence entry.
 *
 * Conflict resolution: Y.Map's LWW semantics give the last writer of
 * `lockedBy` the win. Two peers grabbing simultaneously is a race; the
 * later-timestamped write wins. The loser sees their own lockedBy field
 * overwritten and their UI flips to "locked by ${winner}".
 *
 * Public surface:
 *   - locks               : { [channelIdx]: clientId | null }    (reactive)
 *   - lockHolderInfo(idx) : { clientId, name, color } | null
 *   - isLockedByMe(idx)   : boolean
 *   - isLockedByOther(idx): boolean
 *   - toggleLock(idx)     : function — acquire (if unlocked or self) /
 *                           release (if held by self). No-op if held by
 *                           another peer.
 */

const NO_LOCKS = {};

export default function useChannelLocks({ doc, awareness, awarenessStates, ready }) {
  const [locks, setLocks] = useState(NO_LOCKS);

  /** Recompute the locks snapshot from the live Y.Map. Called on every
   *  Y.Doc change to `channels` and on awareness change (to evict orphans). */
  const recompute = useCallback(() => {
    if (!doc) return;
    const channels = doc.getMap("channels");
    const liveClients = new Set();
    if (awareness && awarenessStates) {
      awarenessStates.forEach((_state, cid) => liveClients.add(cid));
    }
    const next = {};
    channels.forEach((chMap, key) => {
      if (!(chMap instanceof Y.Map)) return;
      const holder = chMap.get("lockedBy");
      if (typeof holder !== "number") return;
      // Evict orphaned locks — holder is no longer present in awareness.
      // This is the only place we proactively delete; we don't observe
      // awareness from within the lock effect because the parent already
      // does that and triggers our recompute via the awarenessStates prop.
      if (!liveClients.has(holder)) {
        try {
          doc.transact(() => { chMap.delete("lockedBy"); }, "lock:evict");
        } catch (e) { /* harmless */ void e; }
        return;
      }
      const idx = Number(key);
      if (Number.isFinite(idx)) next[idx] = holder;
    });
    setLocks(next);
  }, [doc, awareness, awarenessStates]);

  // Subscribe to the channels Y.Map — both top-level (new channel appears)
  // and per-channel-map deep changes (lockedBy field flips).
  useEffect(() => {
    if (!ready || !doc) return undefined;
    const channels = doc.getMap("channels");
    const observers = new Map();

    function attachPerChannel(idx, chMap) {
      if (observers.has(idx)) return;
      const obs = () => recompute();
      chMap.observe(obs);
      observers.set(idx, obs);
    }

    function onChannelsKeyChange(event) {
      event.changes.keys.forEach((change, key) => {
        if (change.action === "add" || change.action === "update") {
          const idx = Number(key);
          const chMap = channels.get(key);
          if (Number.isFinite(idx) && chMap instanceof Y.Map) attachPerChannel(idx, chMap);
        }
      });
      recompute();
    }

    // Initial pass — attach to every existing channel map + compute snapshot.
    channels.forEach((chMap, key) => {
      const idx = Number(key);
      if (Number.isFinite(idx) && chMap instanceof Y.Map) attachPerChannel(idx, chMap);
    });
    channels.observe(onChannelsKeyChange);
    recompute();

    return () => {
      channels.unobserve(onChannelsKeyChange);
      observers.forEach((obs, idx) => {
        const chMap = channels.get(String(idx));
        if (chMap instanceof Y.Map) chMap.unobserve(obs);
      });
      observers.clear();
    };
  }, [doc, ready, recompute]);

  // Also recompute whenever the awareness roster changes — handles the case
  // where a peer disconnects mid-session and we need to evict their locks.
  useEffect(() => {
    recompute();
  }, [awarenessStates, recompute]);

  const myId = awareness?.clientID ?? null;

  const isLockedByMe = useCallback((idx) => locks[idx] != null && locks[idx] === myId, [locks, myId]);
  const isLockedByOther = useCallback((idx) => locks[idx] != null && locks[idx] !== myId, [locks, myId]);

  /** Look up the holder's display name + a stable color from awareness.
   *  Falls back gracefully if the holder dropped off the awareness map. */
  const lockHolderInfo = useCallback((idx) => {
    const holder = locks[idx];
    if (holder == null) return null;
    const state = awarenessStates?.get?.(holder);
    return {
      clientId: holder,
      name: state?.name || "Someone",
      color: state?.color || null,
    };
  }, [locks, awarenessStates]);

  /** Acquire / release a channel lock atomically.
   *  - Unlocked       → acquire (write lockedBy = my clientID)
   *  - Locked by me   → release (delete lockedBy)
   *  - Locked by other → no-op (silently fail; UI shows the holder anyway)
   */
  const toggleLock = useCallback((idx) => {
    if (!doc || myId == null) return null;
    const channels = doc.getMap("channels");
    const key = String(idx);
    let chMap = channels.get(key);
    const current = chMap instanceof Y.Map ? chMap.get("lockedBy") : null;
    if (typeof current === "number" && current !== myId) {
      // Held by someone else — refuse to overwrite. (LWW would technically
      // let us steal, but explicit refusal here keeps the semantics clean
      // and matches the "lock yields exclusive control" mental model.)
      return "refused";
    }
    try {
      doc.transact(() => {
        if (!(chMap instanceof Y.Map)) {
          chMap = new Y.Map();
          channels.set(key, chMap);
        }
        if (current === myId) {
          chMap.delete("lockedBy");
        } else {
          chMap.set("lockedBy", myId);
        }
      });
      return current === myId ? "released" : "acquired";
    } catch (e) {
      console.warn("[locks] toggle failed", e);
      return "error";
    }
  }, [doc, myId]);

  /** Force-release every lock held by me. Called from the leave-room cleanup
   *  so a tab-close doesn't leave phantom locks until the next peer's
   *  recompute evicts them. */
  const releaseAllMine = useCallback(() => {
    if (!doc || myId == null) return;
    const channels = doc.getMap("channels");
    try {
      doc.transact(() => {
        channels.forEach((chMap) => {
          if (chMap instanceof Y.Map && chMap.get("lockedBy") === myId) {
            chMap.delete("lockedBy");
          }
        });
      });
    } catch (e) { console.warn("[locks] releaseAllMine failed", e); }
  }, [doc, myId]);

  return {
    locks,
    isLockedByMe,
    isLockedByOther,
    lockHolderInfo,
    toggleLock,
    releaseAllMine,
    myClientId: myId,
  };
}
